/** The review queue (pending edits, oldest first) and the recent-changes feed. */
import { Link, useSearchParams } from 'react-router'
import { Empty, ErrorState, Loading, Pager, usePage } from '../components/Bits'
import { EditRow } from '../components/EditRow'
import { qs } from '../lib/api'
import { plural } from '../lib/format'
import { useQuery } from '../lib/query'
import { useSession } from '../lib/session'
import { useTitle } from '../lib/useTitle'
import type { EditList } from '../lib/types'

const TABS: Array<{ status: string; label: string; signedIn?: boolean }> = [
  { status: 'pending', label: 'Waiting' },
  { status: 'draft', label: 'My drafts', signedIn: true },
  { status: 'applied', label: 'Applied' },
  { status: 'rejected', label: 'Rejected' },
  { status: 'all', label: 'All' },
]
const TYPES = [
  { value: '', label: 'All records' },
  { value: 'system', label: 'Systems' },
  { value: 'book', label: 'Books' },
  { value: 'publisher', label: 'Publishers' },
  { value: 'creator', label: 'Creators' },
]

function EditFeed({ title, intro, defaultStatus }: { title: string; intro: React.ReactNode; defaultStatus: string }) {
  useTitle(title)
  const session = useSession()
  const [params, setParams] = useSearchParams()
  const page = usePage()
  const status = params.get('status') ?? defaultStatus
  const type = params.get('type') ?? ''
  const mine = params.get('mine') === '1'
  const entity = params.get('entity') ?? ''
  const { data, error, refresh } = useQuery<EditList>(
    `/api/v1/edits${qs({ status, type, entity, author: mine ? session.user?.id : undefined, page, limit: 30 })}`,
    { staleMs: 10_000 },
  )
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params)
    if (v) next.set(k, v)
    else next.delete(k)
    next.delete('page')
    setParams(next, { replace: true })
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{title}</h1>
          <p className="lede">{intro}</p>
        </div>
      </div>
      <div className="filters">
        <div className="seg" role="group" aria-label="Status">
          {TABS.filter((t) => !t.signedIn || session.user).map((t) => (
            <button
              key={t.status}
              type="button"
              aria-pressed={status === t.status}
              onClick={() => set('status', t.status === defaultStatus ? '' : t.status)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <label className="filter">
          <span className="filter-label">Record type</span>
          <select value={type} onChange={(e) => set('type', e.target.value)}>
            {TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        {session.user && (
          <label className="filter filter-toggle">
            <input type="checkbox" checked={mine} onChange={(e) => set('mine', e.target.checked ? '1' : '')} />
            <span>Only my edits</span>
          </label>
        )}
        {entity && (
          <button type="button" className="button button-quiet button-small" onClick={() => set('entity', '')}>
            Showing one record. Show all
          </button>
        )}
      </div>
      {error ? (
        <ErrorState error={error} retry={refresh} />
      ) : !data ? (
        <Loading label="Loading edits" />
      ) : data.items.length === 0 ? (
        <Empty title={status === 'pending' ? 'Nothing waiting for review' : 'No edits here'}>
          {status === 'pending'
            ? 'Every submitted edit has been handled. Thank you, reviewers.'
            : 'Try another filter.'}
        </Empty>
      ) : (
        <>
          <p className="list-count">{plural(data.total, 'edit')}</p>
          <ol className="edit-rows">
            {data.items.map((e) => (
              <li key={e.id}>
                <EditRow edit={e} />
              </li>
            ))}
          </ol>
          <Pager page={data.page} limit={data.limit} total={data.total} />
        </>
      )}
    </div>
  )
}

export function ReviewQueue() {
  const session = useSession()
  return (
    <EditFeed
      title="Review queue"
      defaultStatus="pending"
      intro={
        session.can('editor') ? (
          'Edits from newer contributors, oldest first. Check the sources, then approve or say what needs fixing.'
        ) : (
          <>
            Edits from newer contributors wait here until an editor approves them. Anyone signed in can vote and
            comment; enough approving votes apply an edit on their own. <Link to="/about#review">How review works</Link>
          </>
        )
      }
    />
  )
}

export function RecentChanges() {
  return (
    <EditFeed
      title="Recent changes"
      defaultStatus="applied"
      intro="Everything applied to the database, newest first."
    />
  )
}
